// externals
import * as React from 'react';

// libraries
import { INextStepCardDetails, IVehicleOption } from '@makemydeal/dr-activities-common';
import { IconFactory } from '@makemydeal/dr-activities-common';

// utils
import { formatDollarsAndCents } from '../../../../store/mmd/formatUtils';
import { milesToStringShort } from '../../../../store/mmd/uiHelpersUtil';
import * as tradeInRoutes from '../../../../utils/routes';
import { getBackRouteOfLoanBalance } from '../../../../utils/tradeUtils';

// config
import config from '../../../../store/mmd/config';

// components
import { KBBTradeInAdvisor } from './KBBTradeInAdvisor';
import BackArrowIcon from '../../icons/BackArrowIcon';
import TradePage from '../../../../common/components/TradePage/TradePage';
import GTPFlowPageSubtitle from '../../GTPFlowPageSubtitle/GTPFlowPageSubtitle';
import useGTPRulesModal from '../../../../common/components/subaruEligibility/useGTPRulesModal';
import GTPRulesTrigger from '../../../../common/components/subaruEligibility/GTPRulesTrigger';
import GTPRulesModal from '../../../../common/components/subaruEligibility/GTPRulesModal';

// types
import { ITradeInSubaruGtpFlowError, StepperRoute } from '../../../../types';

export interface ITradeSummaryUIStateProps {
    calculatedTradeInValue: number;
    cardDetails: INextStepCardDetails;
    currentDate: Date;
    dealerPhoneNumber: string;
    expirationDate: string;
    imageFPO: string;
    isSubaruGtpEligible?: boolean;
    isLeadFormEnabled: boolean;
    isLeadFormSubmitted: boolean;
    isManualEntryFlow: boolean;
    isTradeLeadFormEnabled: boolean;
    loanBalance: number;
    make: string;
    model: string;
    stepperRoutes: StepperRoute[];
    subaruGtpLogoAlt?: any; // This is purely for local dev use (storybook can't show the static assets)
    tradeInValue: number;
    tradePaymentFailed: boolean;
    userIsDealer: boolean;
    userIsDesking: boolean;
    valueSource: string;
    vehicleCondition: string;
    vehicleId: string;
    vehicleMileage: number;
    vehicleOptions: IVehicleOption;
    vin?: string;
    year: string;
    zipCode: string;
    ghostVinDealerConfig: boolean;
    isGhostVinEnabled: boolean;
    actionButtonText: string;
    showActionButton: boolean;
    isAmdLienPayoffQuoteSpOnSPEnabled?: boolean;
    selectedLoanBalanceOption?: string;
    isMultiTradeEnabled?: boolean;
    shouldIncludeTradeIn?: boolean;
    hasOpenLoan?: boolean | null;
    tradeInSubaruGtpFlowError?: ITradeInSubaruGtpFlowError;
    tradeIndex?: number;
    urlPath?: string;
}

export interface ITradeSummaryUIDispatchProps {
    dispatchAnalytics: (type: string, description: string) => void;
    editTradeIn: () => void;
    goToRoute: (route: string) => void;
    loadLeadForm: (skiplink: boolean) => void;
    termsDetailsClicked: () => void;
    tradeInEstimateDisplayed: () => void;
    removeTrade: () => void;
    goToNextStep: () => void;
    cdlBackButtonClicked: () => void;
    addRemoveTradeToDeal: (shouldIncludeTradeIn: boolean) => void;
}

export interface ITradeSummaryUIProps extends ITradeSummaryUIStateProps, ITradeSummaryUIDispatchProps {}

const TradeSummaryUI: React.FC<ITradeSummaryUIProps> = (props) => {
    const {
        actionButtonText,
        addRemoveTradeToDeal,
        calculatedTradeInValue,
        cdlBackButtonClicked,
        currentDate,
        dealerPhoneNumber,
        dispatchAnalytics,
        editTradeIn,
        expirationDate,
        ghostVinDealerConfig,
        goToNextStep,
        goToRoute,
        hasOpenLoan,
        isAmdLienPayoffQuoteSpOnSPEnabled,
        isGhostVinEnabled,
        isLeadFormEnabled,
        isLeadFormSubmitted,
        isManualEntryFlow,
        isMultiTradeEnabled,
        isSubaruGtpEligible,
        isTradeLeadFormEnabled,
        loadLeadForm,
        loanBalance,
        make,
        model,
        removeTrade,
        selectedLoanBalanceOption,
        shouldIncludeTradeIn,
        showActionButton,
        stepperRoutes,
        termsDetailsClicked,
        tradeInEstimateDisplayed,
        tradeInSubaruGtpFlowError,
        tradeInValue,
        tradePaymentFailed,
        tradeIndex,
        urlPath,
        userIsDealer,
        userIsDesking,
        valueSource,
        vehicleCondition,
        vehicleId,
        vehicleMileage,
        vehicleOptions,
        year,
        zipCode
    } = props;

    const [showKbbDisclaimer, setShowKbbDisclaimer] = React.useState(false);
    const [showPaymentError, setShowPaymentError] = React.useState(tradePaymentFailed);
    const { showModal, openModal, closeModal } = useGTPRulesModal();

    React.useEffect(() => {
        if (!isLeadFormSubmitted && isLeadFormEnabled) {
            if (!userIsDealer) {
                loadLeadForm(!isTradeLeadFormEnabled);
            } else {
                tradeInEstimateDisplayed();
            }
        } else {
            tradeInEstimateDisplayed();
        }
    }, []);

    const didMountRef = React.useRef(false);
    React.useEffect(() => {
        if (didMountRef.current && isLeadFormSubmitted) {
            tradeInEstimateDisplayed();
        }
        didMountRef.current = true;
    }, [isLeadFormSubmitted]);

    React.useEffect(() => {
        setShowPaymentError(tradePaymentFailed);
    }, [tradePaymentFailed]);

    const isGtpExcluded = isSubaruGtpEligible === false && !!tradeInSubaruGtpFlowError?.errorType;

    const handlePencilEditClicked = () => {
        editTradeIn();
    };

    const toggleKbbDisclaimer = () => {
        termsDetailsClicked();
        setShowKbbDisclaimer(!showKbbDisclaimer);
    };

    const toggleShowPaymentError = () => {
        setShowPaymentError(!showPaymentError);
    };

    const renderError = () => {
        const errorMessage = userIsDealer
            ? 'A payment could not be calculated based on the added trade-in value. Try removing the trade-in to receive a payment. '
            : `Oops, We couldn't calculate a monthly payment based on your trade-in value. Try removing your trade to receive a payment or contact us at ${dealerPhoneNumber}. `;
        return showPaymentError ? (
            <div className="trade-in-payment-failed">
                <i className="fa fa-info-circle"></i>
                <p>
                    {errorMessage}
                    <a href="#" onClick={() => removeTrade()}>
                        Remove Trade-In
                    </a>
                </p>
            </div>
        ) : null;
    };

    const renderTradeConfirmationHeader = () => {
        const { staticImages } = config;
        const className = 'estimated-value__subtext';

        return (
            <div className="trade-in-estimate">
                <IconFactory icon="checkmark" className="icon checkmark" staticImages={staticImages} />
                <div className="estimated-value">
                    <div className="estimated-value__teaser common-header hidden-xs">
                        Your {year} {make} {model} is worth
                    </div>
                    <div className="estimated-value__content common-header" aria-live="polite" aria-atomic="true">
                        <div className="estimated-value__content--label">Estimated Trade-in Value:</div>
                        {calculatedTradeInValue > 0 ? (
                            <div className="estimated-value__content--value positive">
                                {formatDollarsAndCents(calculatedTradeInValue)}
                            </div>
                        ) : null}
                        {calculatedTradeInValue < 0 ? (
                            <div className="estimated-value__content--value negative">
                                {formatDollarsAndCents(calculatedTradeInValue)}
                            </div>
                        ) : null}
                    </div>
                    <div className={className}>
                        Your trade-in equity is the net of your Trade-In Value and any Outstanding Loan amount
                    </div>
                </div>
                {loanBalance > 0 && (
                    <div className="math-stack-container">
                        <div className="math-stack">
                            <div>
                                <div className="math-stack-label">{!isManualEntryFlow ? 'KBB Value' : 'Entered Value'}:</div>
                                <div className="math-stack-value">{formatDollarsAndCents(tradeInValue)}</div>
                            </div>
                            <div>
                                <div className="math-stack-label">Loan Balance:</div>
                                <div className="math-stack-value">{formatDollarsAndCents(loanBalance)}</div>
                            </div>
                            <div>
                                <div className="math-stack-label">Trade-In Value:</div>
                                {calculatedTradeInValue > 0 ? (
                                    <div className="math-stack-value positive">{formatDollarsAndCents(calculatedTradeInValue)}</div>
                                ) : null}
                                {calculatedTradeInValue < 0 ? (
                                    <div className="math-stack-value negative">{formatDollarsAndCents(calculatedTradeInValue)}</div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Note: the intro phrase below ("We've applied your estimated trade-in value to your deal.") is not
    // literally specified by the ticket's AC/snippet — it mirrors ManualSummaryUI's pageSubTitle() suffix
    // pattern, adapted for the ICO fallback flow's copy. See dev-session.md for rationale.
    const pageSubTitle = () => (
        <GTPFlowPageSubtitle
            pageSubtitle="Determine how much your current vehicle is worth, and apply that value to your new vehicle."
            suffix={
                <>
                    We've applied your estimated trade-in value to your deal.
                    {isGtpExcluded && (
                        <span className="trade-in-vehicle__gtprules">
                            &nbsp;See <GTPRulesTrigger onOpen={openModal} />
                        </span>
                    )}
                </>
            }
            tradeIndex={tradeIndex}
        />
    );

    const { staticImages, kbbTradeInApiKey, renderMenuProducts } = config;
    const columnSize = 'col-sm-5';
    const pencilIcon = <IconFactory icon="pencilIcon" className="icon primary-link-icon" staticImages={staticImages} />;
    const editText = <span className="primary-link">Edit</span>;

    let dealerDealDescriptionText = '';
    let dealerDealLinkText = '';
    const backButtonRoute = isAmdLienPayoffQuoteSpOnSPEnabled
        ? getBackRouteOfLoanBalance(selectedLoanBalanceOption, hasOpenLoan)
        : tradeInRoutes.TRADE_EQUIPMENT_OPTIONS;

    if (isGhostVinEnabled) {
        dealerDealDescriptionText = ghostVinDealerConfig ? 'Submit the completed deal to the CRM for review' : '';
        dealerDealLinkText = ghostVinDealerConfig ? 'Send to CRM' : 'Submit for Review';
    }

    const handleSkipButtonClick = () => {
        removeTrade();
    };

    const handleBackButtonClick = () => {
        cdlBackButtonClicked();
        goToRoute(backButtonRoute);
    };

    return (
        <>
            <TradePage
                urlPath={urlPath}
                pageClass="trade-in-summary-page"
                pageTitle="Trade-In Vehicle"
                headerOnTop
                showCustomTitle
                pageSubTitle={pageSubTitle()}
                isSubaruGtpEligible={isSubaruGtpEligible}
                tradeInSubaruGtpFlowError={tradeInSubaruGtpFlowError}
                showShareDeal={false}
                isDealerUser={userIsDealer}
                isDeskingUser={userIsDesking}
                staticAssetsConfig={staticImages}
                cardDetails={null}
                goToRoute={goToRoute}
                dispatchAnalytics={dispatchAnalytics}
                renderError={renderError}
                stepperRoutes={stepperRoutes}
                stepLabelVisible={true}
                isStepperClickable={false}
                dealerDealDescription={dealerDealDescriptionText}
                dealerDealLinkText={dealerDealLinkText}
                {...(!isMultiTradeEnabled && {
                    footerProps: {
                        onActionButtonClick: showActionButton ? goToNextStep : undefined,
                        buttonText: actionButtonText,
                        showBackButton: true,
                        showSkipButton: true,
                        skipButtonText: 'Remove Trade-In',
                        fullWidthLayout: true,
                        onSkipButtonClick: handleSkipButtonClick,
                        onBackButtonClick: handleBackButtonClick
                    }
                })}
            >
                <div className="trade-in-summary-content">
                    {renderTradeConfirmationHeader()}
                    <div className="trade-in-vehicle container-fluid">
                        <div className="row">
                            <div className={`${columnSize} container vehicle-info-details`}>
                                <div className="title common-header">
                                    Your Trade-In
                                    <div
                                        className="primary-link-container subaru-mobile-view"
                                        onClick={handlePencilEditClicked}
                                    ></div>
                                </div>
                                <div className="vehicle-info common-header">
                                    <div className="vehicle-info__ymm">
                                        {year} {make} {model}
                                        <div className="primary-link-container pencil-edit-icon" onClick={handlePencilEditClicked}>
                                            {pencilIcon}
                                            {editText}
                                        </div>
                                    </div>
                                </div>
                                <div className="vehicle-info__content">
                                    <div className="vehicle-info__contentLeft">
                                        {isManualEntryFlow && (
                                            <div>
                                                Value Provided by: <span>{valueSource}</span>
                                            </div>
                                        )}
                                        <div>
                                            Condition: <span>{vehicleCondition}</span>
                                        </div>
                                        <div>
                                            Mileage: <span>{milesToStringShort(vehicleMileage)}</span>
                                        </div>
                                        <div>
                                            Zip Code: <span>{zipCode}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {!isManualEntryFlow && (
                                <div className="col-sm-7 vehicle-info-kbb-value">
                                    <div className="kbb-trade-in-advisor-container">
                                        <KBBTradeInAdvisor
                                            apiKey={kbbTradeInApiKey}
                                            vehicleId={vehicleId}
                                            vehicleOptions={vehicleOptions as unknown as Record<string, IVehicleOption>}
                                            zipCode={zipCode}
                                            vehicleMileage={vehicleMileage}
                                            vehicleCondition={vehicleCondition}
                                            showExactValue={false}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        {!isManualEntryFlow && (
                            <div className="row">
                                <div className="col-xs-12 trade-in-vehicle__tc">
                                    Actual price of this vehicle may vary. Estimate valid through&nbsp;
                                    {new Date(expirationDate).toLocaleDateString()}. &nbsp;
                                    <a className="primary-link" onClick={toggleKbbDisclaimer}>
                                        {showKbbDisclaimer ? 'Hide Terms and Conditions' : 'View Terms and Conditions'}
                                    </a>
                                    {showKbbDisclaimer && (
                                        <p className="kbb-trade-in-advisor-disclaimer">
                                            © {currentDate.getFullYear()} Kelley Blue Book Co., Inc. All rights reserved.{' '}
                                            {new Date(expirationDate).toLocaleDateString()} Edition for {zipCode}. Values and
                                            pricing are the opinions of Kelley Blue Book, and the actual price of a vehicle may
                                            vary. The value and pricing information displayed for a particular vehicle is based upon
                                            the specification, mileage and/or condition information provided by the person
                                            generating this report. Kelley Blue Book assumes no responsibility for errors or
                                            omissions.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {renderMenuProducts()}
            </TradePage>
            {isMultiTradeEnabled && (
                <div className="trade-summary-footer">
                    <button className="back-button" onClick={handleBackButtonClick}>
                        <BackArrowIcon />
                        Back
                    </button>
                    <div className="right-actions">
                        <div className="apply-trade-toggle">
                            <label className="toggle-label">
                                Apply Trade to Deal
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={shouldIncludeTradeIn}
                                        onChange={() => addRemoveTradeToDeal(shouldIncludeTradeIn)}
                                    />
                                    <span className="slider"></span>
                                </div>
                            </label>
                        </div>
                        <button className="delete-trade-button" onClick={handleSkipButtonClick}>
                            Delete Trade-In
                        </button>
                    </div>
                </div>
            )}
            {isGtpExcluded && <GTPRulesModal show={showModal} onClose={closeModal} dispatchAnalytics={dispatchAnalytics} />}
        </>
    );
};

export default TradeSummaryUI;
